import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Trophy, Medal, ArrowRight } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

interface LeaderboardPreviewProps {
  companyId?: string | null;
}

export function LeaderboardPreview({ companyId }: LeaderboardPreviewProps) {
  const navigate = useNavigate();

  const { data: topPerformers = [], isLoading } = useQuery({
    queryKey: ['leaderboard-preview', companyId],
    queryFn: async () => {
      let query = supabase
        .from('leaderboard_all_time')
        .select('*')
        .not('average_score', 'is', null)
        .order('average_score', { ascending: false })
        .limit(5);

      if (companyId) {
        query = query.eq('company_id', companyId);
      }

      const { data } = await query;
      if (!data || data.length === 0) return [];

      // Find entries with missing user_name and fetch from profiles
      const missingNameUserIds = data
        .filter(d => !d.user_name)
        .map(d => d.user_id)
        .filter(Boolean);

      let profileMap: Record<string, string> = {};
      if (missingNameUserIds.length > 0) {
        const { data: profiles } = await supabase
          .from('profiles')
          .select('id, display_name, email')
          .in('id', missingNameUserIds);
        
        profiles?.forEach(p => {
          profileMap[p.id] = p.display_name || p.email?.split('@')[0] || 'Unknown';
        });
      }

      // Enrich entries with fallback names
      return data.map(entry => ({
        ...entry,
        user_name: entry.user_name || profileMap[entry.user_id] || 'Unknown User',
      }));
    },
  });

  const getRankIcon = (rank: number) => {
    if (rank === 1) return <Trophy className="h-5 w-5 text-amber-500" />;
    if (rank === 2) return <Medal className="h-5 w-5 text-slate-400" />;
    if (rank === 3) return <Medal className="h-5 w-5 text-amber-700" />;
    return <span className="w-5 h-5 flex items-center justify-center text-sm text-muted-foreground">{rank}</span>;
  };

  const getInitials = (name: string | null) => {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Trophy className="h-5 w-5" />
            Top Performers
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-[200px] flex items-center justify-center text-muted-foreground">
            Loading...
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
        <div>
          <CardTitle className="flex items-center gap-2">
            <Trophy className="h-5 w-5 text-amber-500" />
            Top Performers
          </CardTitle>
          <CardDescription>Highest average scores</CardDescription>
        </div>
        <Button variant="ghost" size="sm" onClick={() => navigate('/leaderboards')} className="gap-1">
          View All
          <ArrowRight className="h-4 w-4" />
        </Button>
      </CardHeader>
      <CardContent>
        {topPerformers.length === 0 ? (
          <div className="h-[200px] flex items-center justify-center text-muted-foreground">
            No leaderboard data yet
          </div>
        ) : (
          <div className="space-y-3">
            {topPerformers.map((performer, index) => (
              <div
                key={`${performer.user_id}-${performer.agent_id}`}
                className="flex items-center gap-3 p-2 rounded-lg hover:bg-muted/50 transition-colors"
              >
                <div className="w-6 flex justify-center">
                  {getRankIcon(index + 1)}
                </div>
                <Avatar className="h-8 w-8">
                  <AvatarImage src={performer.avatar_url || undefined} />
                  <AvatarFallback className="text-xs">
                    {getInitials(performer.user_name)}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">
                    {performer.user_name || 'Unknown'}
                  </p>
                  <p className="text-xs text-muted-foreground truncate">
                    {performer.agent_name || 'All agents'}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-sm font-semibold text-primary">
                    {Math.round(performer.average_score || 0)}%
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {performer.total_sessions || 0} sessions
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
